import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import htmlAppletService from '../../services/htmlAppletService';
import { formatTime } from '../../utils/formatters';

const DEFAULT_PAGE_SIZE = 50;

type Props = {
  appletId: string;
  appletName?: string | null;
  /** Show export buttons (requires ViewHtmlAppletResults). */
  showExport?: boolean;
  /** Optional fixed height for the grid. */
  height?: number | string;
};

/**
 * Server-paged HtmlApplet results table (avoids loading thousands of rows at once).
 */
const HtmlAppletResultsPanel = ({
  appletId,
  showExport = true,
  height = 420,
}: Props) => {
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // DataGrid is 0-based
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  const loadPage = useCallback(
    (nextPage: number, nextPageSize: number) => {
      if (!appletId) return;
      setLoading(true);
      dispatch({ type: 'SHOW_QUERY' });
      htmlAppletService
        .listResults(appletId, {
          page: nextPage + 1,
          pageSize: nextPageSize,
        })
        .then((response) => {
          if (!response.hasError) {
            setRows(response.records ?? []);
            setTotalCount(response.totalCount ?? 0);
          } else {
            enqueueSnackbar((response.errorMessages ?? []).join(','), {
              variant: 'error',
            });
          }
        })
        .catch((err) => {
          enqueueSnackbar(err?.message || 'Eredmények betöltése sikertelen', {
            variant: 'error',
          });
        })
        .finally(() => {
          setLoading(false);
          dispatch({ type: 'HIDE' });
        });
    },
    [appletId, dispatch, enqueueSnackbar]
  );

  useEffect(() => {
    setPage(0);
    loadPage(0, pageSize);
  }, [appletId]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportResults = (format: 'jsonl' | 'xlsx') => {
    dispatch({ type: 'SHOW_QUERY' });
    htmlAppletService
      .exportResults(appletId, format)
      .then(() => enqueueSnackbar('Export kész!', { variant: 'success' }))
      .catch((err) =>
        enqueueSnackbar(err?.message || 'Export sikertelen', {
          variant: 'error',
        })
      )
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  const columns: any[] = [
    {
      field: 'createdAt',
      headerName: 'Létrehozva',
      flex: 1,
      minWidth: 160,
      valueFormatter: (value: any) => formatTime(value ?? '', ''),
    },
    {
      field: 'payload',
      headerName: 'Payload',
      flex: 3,
      minWidth: 240,
      valueFormatter: (value: any) => {
        const text = String(value ?? '');
        return text.length > 160 ? `${text.substring(0, 160)}…` : text;
      },
    },
  ];

  return (
    <Box>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1
        }}>
        <Grid>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Összesen: {totalCount} válasz
            {totalCount > pageSize
              ? ` (oldalanként max ${pageSize})`
              : ''}
          </Typography>
        </Grid>
        {showExport && (
          <Grid>
            <Button
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
              disabled={!appletId || totalCount === 0}
              onClick={() => exportResults('jsonl')}
            >
              Raw (JSONL)
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!appletId || totalCount === 0}
              onClick={() => exportResults('xlsx')}
            >
              Excel export
            </Button>
          </Grid>
        )}
      </Grid>
      <Box sx={{ height, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
            loadPage(model.page, model.pageSize);
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
    </Box>
  );
};

export default HtmlAppletResultsPanel;
